import { httpClientAuthorizer, httpClient, getLocale } from "@sia/style-guide";
import keycloakIDPAdapter from "../IDPAdapter/KeycloakIDPAdapter";
import { EncryptionProvider } from "common/crypto";

const API_BASE_URL = process.env.REACT_APP_EVALUATION_API_BASE_URL;

class EvaluationApiClient {
  constructor(client) {
    this.baseUrl = API_BASE_URL;
    this.httpClient = client;
  }

  getCompanyHashHeaders(companyAccess = false) {
    if (!companyAccess) return {};
    return { companyHash: EncryptionProvider.requireSnapshot().companyHash };
  }

  getLocalizedUrl(endpoint, lang) {
    const endpointWithoutLangSuffix = ["overhead-costs", "business-key-figures"];
    if (endpointWithoutLangSuffix.includes(endpoint)) {
      return `${this.baseUrl}/${endpoint}`;
    }
    return `${this.baseUrl}/${endpoint}/${getLocale(lang)}`;
  }

  async request(method, url, data, onError, ignoreLoader = false, headers = {}) {
    if (!keycloakIDPAdapter.isLoggedIn()) return undefined;
    try {
      await keycloakIDPAdapter.getClient().updateToken(0);
      return await this.httpClient.asyncRequest({
        method,
        url,
        data,
        config: httpClientAuthorizer.authorizeRequestBenchmarkingConfig(keycloakIDPAdapter, { headers }),
        onError,
        ignoreLoader,
      });
    } catch (error) {
      keycloakIDPAdapter.logout(window.location.href, true);
      return error;
    }
  }

  post(endpoint, lang, data, onError, companyAccess = false) {
    return this.request("POST", this.getLocalizedUrl(endpoint, lang), data, onError, false, this.getCompanyHashHeaders(companyAccess));
  }

  get(endpoint, onError, headers = {}) {
    return this.request("GET", `${this.baseUrl}/${endpoint}`, null, onError, false, headers);
  }

  getWages(lang, data, onError, companyAccess = false) {
    return this.post("wage-costs", lang, data, onError, companyAccess);
  }

  getOverheads(lang, data, onError, companyAccess = false) {
    return this.post("overhead-costs", lang, data, onError, companyAccess);
  }

  getKeyFigures(lang, data, onError, companyAccess = false) {
    return this.post("business-key-figures", lang, data, onError, companyAccess);
  }

  getWorkingHours(lang, data, onError, companyAccess = false) {
    return this.post("working-hours", lang, data, onError, companyAccess);
  }

  getCalculations(lang, data, onError, companyAccess = false) {
    return this.post("hourly-rate-calculator", lang, data, onError, companyAccess);
  }

  getCompanyDetail(onError, companyAccess) {
    if (!companyAccess) throw new Error("Company detail requires explicit company access");
    return this.get("company-detail", onError, this.getCompanyHashHeaders(true));
  }

  getAllOfficeSizes(lang, onError) {
    return this.request("GET", `${this.baseUrl}/company-detail/office-sizes/${getLocale(lang)}`, null, onError, false, {});
  }
  
  getAllNogas(lang, onError) {
    return this.request("GET", `${this.baseUrl}/specializations-noga/${getLocale(lang)}`, null, onError, false, {});
  }

  getNogaDetail(lang, nogaId, onError) {
    return this.request("GET", `${this.baseUrl}/specializations-noga/${nogaId}/${getLocale(lang)}`, null, onError, false, {});
  }
}

const evaluationApiClient = new EvaluationApiClient(httpClient);
export default evaluationApiClient;
